import React, { useState, useEffect } from 'react';
import analyticsService from '../utils/analytics';
import { useAuth } from '../hooks/useAuth';
import './CookieConsent.css';

const CookieConsent = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const checkConsent = async () => {
      // Helper function to get cookie value
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
      };

      // If user is authenticated, check if they need to consent
      if (isAuthenticated && user) {
        try {
          const response = await fetch('/api/user/analytics-consent', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

          if (response.ok) {
            const data = await response.json();

            // If user hasn't set consent yet, only auto-grant if they're an existing user (not a new signup)
            if (!data.hasConsent) {
              const isNewSignup = sessionStorage.getItem('isNewSignup');

              // Only auto-grant for existing users, new signups need to explicitly consent
              if (isNewSignup !== 'true') {
                console.log('[Analytics Consent] Auto-granting consent for existing authenticated user');
                await fetch('/api/user/analytics-consent', {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ analyticsConsent: true })
                });

                // Sync to local cookie
                const expires = new Date();
                expires.setFullYear(expires.getFullYear() + 1);
                document.cookie = `analytics_consent=accepted; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

                analyticsService.initialize();
                setIsLoading(false);
                return;
              } else {
                // New signup - show the consent banner
                setShowConsent(true);
                setIsLoading(false);
                return;
              }
            }

            // User has already made a decision
            const consent = data.analyticsConsent ? 'accepted' : 'declined';

            // Sync to local cookie
            const expires = new Date();
            expires.setFullYear(expires.getFullYear() + 1);
            document.cookie = `analytics_consent=${consent}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

            if (consent === 'accepted') {
              analyticsService.initialize();
            }

            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error checking server consent:', error);
          // Fall through to local check
        }
      }

      // Check cookie first, then localStorage for backwards compatibility
      let consent = getCookie('analytics_consent');
      if (!consent) {
        consent = localStorage.getItem('analytics_consent');
        // If found in localStorage, sync to cookie
        if (consent) {
          const expires = new Date();
          expires.setFullYear(expires.getFullYear() + 1);
          document.cookie = `analytics_consent=${consent}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
        }
      }

      if (!consent) {
        // Show consent banner if no decision has been made
        setShowConsent(true);
      } else if (consent === 'accepted') {
        // Initialize analytics if already consented
        analyticsService.initialize();
      }

      setIsLoading(false);
    };

    checkConsent();
  }, [isAuthenticated, user]);

  const handleAccept = async () => {
    // Save consent decision locally
    analyticsService.setConsent(true);

    // If authenticated, also save to server
    if (isAuthenticated) {
      try {
        await fetch('/api/user/analytics-consent', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ analyticsConsent: true })
        });
      } catch (error) {
        console.error('Error saving consent to server:', error);
      }
    }

    sessionStorage.removeItem('isNewSignup'); // Clear the flag after consent
    setShowConsent(false);
  };

  const handleDecline = async () => {
    // Save decline decision locally
    analyticsService.setConsent(false);

    // If authenticated, also save to server
    if (isAuthenticated) {
      try {
        await fetch('/api/user/analytics-consent', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ analyticsConsent: false })
        });
      } catch (error) {
        console.error('Error saving consent to server:', error);
      }
    }

    sessionStorage.removeItem('isNewSignup'); // Clear the flag after consent
    setShowConsent(false);
  };

  // Don't render anything while loading or if consent already given
  if (isLoading || !showConsent) {
    return null;
  }

  return (
    <div className="cookie-consent">
      <div className="cookie-consent-content">
        <div className="cookie-consent-text">
          <h3>🍪 Cookie Consent</h3>
          <p>
            We use analytics cookies to understand how you use our service and improve it.
            These cookies help us track usage patterns, identify issues, and enhance your experience.
          </p>
          <p className="cookie-consent-privacy">
            We respect your privacy. Analytics data is anonymized and never sold to third parties.
            <a href="/privacy" target="_blank" rel="noopener noreferrer"> Learn more</a>
          </p>
        </div>
        <div className="cookie-consent-actions">
          <button
            className="cookie-consent-button cookie-consent-decline"
            onClick={handleDecline}
          >
            Decline
          </button>
          <button
            className="cookie-consent-button cookie-consent-accept"
            onClick={handleAccept}
          >
            Accept Analytics
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;